import { useState } from 'react';
import { Plus, ClipboardList } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { ChecklistFillModal } from './ChecklistFillModal';
import { ChecklistFormModal } from './ChecklistFormModal';

interface ComissaoChecklistSectionProps {
  comissaoId: string;
  comissaoNome?: string;
  comissaoMembroId?: string;
}

export function ComissaoChecklistSection({
  comissaoId,
  comissaoNome,
  comissaoMembroId,
}: ComissaoChecklistSectionProps) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ADMIN', 'COORDENADOR_EVENTO');

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isFillModalOpen, setIsFillModalOpen] = useState(false);
  const [selectedChecklistId, setSelectedChecklistId] = useState<string | null>(null);

  const { data: checklistsData, isLoading } = trpc.checklists.list.useQuery({
    comissaoId,
    page: 1,
    limit: 100,
  });
  const checklists = checklistsData?.items;

  const handleClickChecklist = (checklistId: string) => {
    setSelectedChecklistId(checklistId);
    setIsFillModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setIsFormModalOpen(false);
  };

  const handleCloseFillModal = () => {
    setIsFillModalOpen(false);
    setSelectedChecklistId(null);
  };

  return (
    <div className="border-t border-gray-100 pt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Checklists ({checklists?.length || 0})
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsFormModalOpen(true)}
          disabled={!canEdit}
          className={`text-xs ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Plus className="h-3 w-3 mr-1" />
          Adicionar
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
        </div>
      ) : checklists && checklists.length > 0 ? (
        <div className="space-y-2">
          {checklists.map((checklist) => (
            <ChecklistItem
              key={checklist.id}
              checklistId={checklist.id}
              nome={checklist.nome}
              comissaoMembroId={comissaoMembroId}
              onClick={() => handleClickChecklist(checklist.id)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-gray-400">
          <ClipboardList className="h-6 w-6 mx-auto mb-1" />
          <p className="text-xs">Nenhum checklist</p>
        </div>
      )}

      {/* Form Modal for creating new checklist */}
      <ChecklistFormModal
        isOpen={isFormModalOpen}
        onClose={handleCloseFormModal}
        comissoes={[{ id: comissaoId, nome: comissaoNome || 'Comissão' }]}
        defaultComissaoId={comissaoId}
      />

      {/* Fill Modal */}
      {selectedChecklistId && (
        <ChecklistFillModal
          isOpen={isFillModalOpen}
          onClose={handleCloseFillModal}
          checklistId={selectedChecklistId}
          comissaoMembroId={comissaoMembroId}
        />
      )}
    </div>
  );
}

// Sub-component for each checklist item with progress
interface ChecklistItemProps {
  checklistId: string;
  nome: string;
  comissaoMembroId?: string;
  onClick: () => void;
}

function ChecklistItem({ checklistId, nome, comissaoMembroId, onClick }: ChecklistItemProps) {
  const { data: checklist } = trpc.checklists.getById.useQuery({ id: checklistId });

  const { data: resposta } = trpc.checklists.getResposta.useQuery(
    { checklistId, comissaoMembroId: comissaoMembroId! },
    { enabled: !!comissaoMembroId }
  );

  // Calculate progress
  const totalItens = checklist?.secoes.reduce((acc, s) => acc + s.itens.length, 0) || 0;
  const preenchidos = comissaoMembroId && resposta?.valores
    ? Object.keys(resposta.valores).filter(
        (k) => (resposta.valores as Record<string, any>)[k]?.valor !== null &&
               (resposta.valores as Record<string, any>)[k]?.valor !== undefined
      ).length
    : 0;
  const progressPercent = totalItens > 0 ? Math.round((preenchidos / totalItens) * 100) : 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 p-2 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-colors text-left"
    >
      <ClipboardList className="h-4 w-4 text-gray-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{nome}</p>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                progressPercent === 100 ? 'bg-green-500' : 'bg-primary-500'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="text-xs text-gray-500 flex-shrink-0">
            {preenchidos}/{totalItens}
          </span>
        </div>
      </div>
    </button>
  );
}
